import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import SEO from '@/components/SEO';
import StructuredData, { schemas } from '@/components/SEO/StructuredData';
import { SmsOptInCheckbox } from '@/components/SmsConsentDisclosure';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    message: '',
    sms_opt_in: false,
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/contact', formData);
      toast.success('Your message has been sent! We\'ll respond within 24 hours.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        department: '',
        message: '',
        sms_opt_in: false,
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <SEO 
        title="Contact Us | Bespoke Realty Memphis & North Mississippi"
        description="Get in touch with Bespoke Realty for all your real estate needs. Located in Memphis, TN, we serve Southaven, Olive Branch, Collierville, Germantown, and surrounding areas. Call or visit us today!"
        keywords="contact Bespoke Realty, Memphis realtor phone, real estate office Memphis TN, Southaven real estate contact, Mississippi Tennessee real estate agent"
      />
      <StructuredData schema={[schemas.contactPage(), schemas.breadcrumb([{ name: 'Home', url: '/' }, { name: 'Contact', url: '/contact' }])]} />
      {/* Hero Banner */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_agent-platform-28/artifacts/i5pgts2z_contactus.png)',
          }}
        ></div>
        <div className="relative z-20 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold mb-4 text-white" data-testid="contact-heading">Contact Us</h1>
          <p className="text-xl text-white">We're here to help with all your real estate needs</p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold mb-6">Get in Touch</h2>
              <p className="text-gray-600 mb-8">
                Whether you're buying, selling, or have questions about our services, our team is ready to assist you.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#BCA480]/10 rounded-lg">
                    <MapPin className="h-6 w-6 text-[#BCA480]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Office Address</h3>
                    <p className="text-gray-600 font-semibold">Bespoke Realty</p>
                    <p className="text-gray-600">7927 Players Forest Drive</p>
                    <p className="text-gray-600">Memphis, TN 38119</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#BCA480]/10 rounded-lg">
                    <Phone className="h-6 w-6 text-[#BCA480]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Phone</h3>
                    <p className="text-gray-600">Office: <a href="tel:9013904414" className="hover:text-[#BCA480]">901-390-4414</a></p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-[#BCA480]/10 rounded-lg">
                    <Mail className="h-6 w-6 text-[#BCA480]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Office Hours</h3>
                    <p className="text-gray-600">Monday - Friday: 10:30 AM - 4:30 PM</p>
                    <p className="text-gray-600">Saturday & Sunday: By Appointment Only</p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-bold text-sm mb-2">Licensing Information</h3>
                  <p className="text-xs text-gray-600">MS License #24018</p>
                  <p className="text-xs text-gray-600">TN License #266066</p>
                  <p className="text-xs text-gray-600">AR Principal Broker License #PB000099531</p>
                </div>
              </div>

              {/* Google Map - Office Location */}
              <div className="mt-8 h-64 bg-gray-200 rounded-lg overflow-hidden">
                <iframe
                  title="Bespoke Realty Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3263.0901831776447!2d-89.85098492346877!3d35.08673987280621!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x887f87e5c7c7c7c7%3A0x0!2s7927%20Players%20Forest%20Dr%2C%20Memphis%2C%20TN%2038119!5e0!3m2!1sen!2sus!4v1705600000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h2 className="text-3xl font-bold mb-6" data-testid="contact-form-heading">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    data-testid="contact-phone-input"
                  />
                  <SmsOptInCheckbox
                    checked={formData.sms_opt_in}
                    onChange={(v) => setFormData({ ...formData, sms_opt_in: v })}
                  />
                </div>

                <div>
                  <Label htmlFor="department">Department *</Label>
                  <Select name="department" value={formData.department} onValueChange={(value) => setFormData({...formData, department: value})}>
                    <SelectTrigger data-testid="contact-department-select">
                      <SelectValue placeholder="Select a department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buy">Buying a Home</SelectItem>
                      <SelectItem value="sell">Selling a Home</SelectItem>
                      <SelectItem value="property_management">Property Management</SelectItem>
                      <SelectItem value="join">Join Our Team</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    required
                    data-testid="contact-message-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-[#BCA480] hover:bg-[#a89470]"
                  data-testid="contact-submit-btn"
                >
                  Send Message
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
